/* eslint-disable react/display-name */
import React, { FC, ReactElement, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  FormControl,
  FormControlLabel,
  FormLabel,
  ListItemText,
  makeStyles,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Theme,
  Typography
} from '@material-ui/core';
import {
  AddCircle as AddCircleIcon,
  Close as CloseIcon,
  Remove as RemoveIcon
} from '@material-ui/icons';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import Fab from 'components/@common/Fab';
import DatePicker from 'components/@common/Inputs/DatePicker';
import useKeySetting from 'components/ClassroomsModule/hooks/use-key-setting';
import { format } from 'date-fns';
import UPDATE_KEY_CLASSROOM_ASSESSMENT_ACCELERATOR_MODULES_MUTATION from 'graph/classroom/update-key-classroom-assessment-accelerator-modules-mutation';
import GET_KEYBOARDING_CLASSROOM_SETTINGS from 'graph/settings/key/get-keyboarding-classroom-settings-query';
import { updateCache } from 'graph/update-cache';
import { AlertVariant } from 'types/enums';
import { Classroom, ScheduledActivityScheduleType } from 'types/schema-generated-types';
import useStore from 'store';
import getSettingSuccessAlert from '../../../../utils/settings-success-alert';
import ModuleCard from './ModuleCard';
import useAssessmentScheduler from './use-assessment-scheduler';
import {
  assetMap,
  buildModuleMap,
  buildMultiSelected,
  filterNillFromModules,
  getHasChecked,
  omitLast
} from './utils';

const DATE_FORMAT = 'MM/dd/yyyy';

interface KeyboardingAssessmentsInputs {
  label: string;
  value: ScheduledActivityScheduleType | 'NONE';
}

interface KeyboardingAssessmentProps {
  classroom: Classroom;
  expanded: string | null;
  setting: any;
  handleChangeExpanded: (panel: string | null) => () => void;
  refetch: () => void;
}

const assessmentsOptions: KeyboardingAssessmentsInputs[] = [
  {
    label: "Don't schedule Assessments",
    value: 'NONE'
  },
  {
    label: 'Schedule assessments on specific dates.',
    value: ScheduledActivityScheduleType.CALENDAR
  }
];

const getClassroomKeySettingsMutationCacheUpdate = (classroomId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_KEYBOARDING_CLASSROOM_SETTINGS,
      variables: {
        where: {
          classroomId
        }
      },
      reducer: (
        cachedData,
        { updateKeyClassroomSettingAssessments }
      ): { keyClassroomSettings: any } => {
        return {
          keyClassroomSettings: {
            ...cachedData?.keyClassroomSettings,
            assessmentSettings: updateKeyClassroomSettingAssessments
          }
        };
      }
    }
  ])
});

const AssessmentAccelerator: FC<KeyboardingAssessmentProps> = ({
  setting: settingValue,
  classroom,
  expanded,
  handleChangeExpanded,
  refetch
}) => {
  const { id: classroomId } = classroom;
  const { t } = useTranslation('AssessmentSetting');
  const classes = useStyles();
  const [, { openAlert }] = useStore();
  const [
    state,
    { onAddDate, onDateChange, onDeleteDate, onSelectionChange, refreshState }
  ] = useKeySetting({});

  const [
    { modules, current, currentKeyboardingModules, futureDates, ...mState },
    {
      addAssessmentModule,
      updateModule,
      deleteModule,
      getScheduledData,
      setFutureModule,
      deleteFutureModule,
      reset
    }
  ] = useAssessmentScheduler(settingValue);

  const [updateAssessmentAcceleratorModules] = useMutation(
    UPDATE_KEY_CLASSROOM_ASSESSMENT_ACCELERATOR_MODULES_MUTATION,
    getClassroomKeySettingsMutationCacheUpdate(+classroomId)
  );

  const handleOnCancel = async () => {
    refreshState({});
    reset();
    await refetch();
    setFutureModule(settingValue?.scheduledDates, true);
  };

  const handleModuleChange = (idx: any, selectedDate: any) => (e: any) => {
    updateModule(idx, e.target.value);
  };

  const handleDelete = (idx: number) => () => {
    deleteModule(idx);
    onDeleteDate(idx)();
  };

  const handleOnConfirm = async (): Promise<void> => {
    try {
      if (state.scheduleType === 'NONE') {
        openAlert({
          variant: AlertVariant.WARNING,
          title: t(
            'Warning! This will only remove assessments in the future. All other scheduled assessments will remain in place'
          ),
          onSubmit: handleRemoveAllAssessments,
          confirmButtonText: t('Yes, remove')
        });
        return;
      }
      const scheduledDates = getScheduledData(state.scheduledDates);

      await updateAssessmentAcceleratorModules({
        variables: {
          data: {
            classroomId,
            scheduledDates
          }
        }
      });

      await refreshAll();
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemoveAllAssessments = async () => {
    await updateAssessmentAcceleratorModules({
      variables: {
        data: {
          classroomId,
          scheduledDates: []
        }
      }
    });
    // setOpenConfirmation(false);

    await refreshAll();
  };

  const refreshAll = async () => {
    handleChangeExpanded(null)();
    await refreshState({});
    await reset();
    await refetch();
    openAlert(getSettingSuccessAlert('Assessment Accelerator', t));
  };

  const getModuleFromIds = (moduleIds: number[], date: string) => {
    if (!currentKeyboardingModules) return [];
    const filteredIds = moduleIds?.map(mid => currentKeyboardingModules?.find(x => x.id === mid));
    const moduleMap = buildModuleMap(filteredIds, date);

    return filterNillFromModules(modules!, moduleMap);
  };

  useEffect(() => {
    if (!settingValue?.scheduledDates) return;
    setFutureModule(settingValue?.scheduledDates);
    // default selects schedule radio
    if (settingValue?.scheduledDates?.length)
      onSelectionChange({ target: { value: assessmentsOptions[1].value } } as any);

    // eslint-disable-next-line
  }, [settingValue]);

  const getDisabled = () => {
    if (state.scheduleType === 'NONE') return false;
    return mState?.deletedModule ? !mState?.deletedModule : getHasChecked(mState);
  };

  const buildSrc = (assetName: string) => {
    return `/images/${assetName}.png`;
  };

  return (
    <SettingsCard
      disabled={getDisabled()}
      title={'Assessment Accelerator'}
      panel="AssessmentAccelerator"
      expanded={expanded}
      handleChangeExpanded={handleChangeExpanded}
      description={t('Description')}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
      closeOnSubmit={false}
    >
      <Fragment>
        <Grid container spacing={2}>
          {omitLast(modules)?.map((x: any) => (
            <Grid item>
              <ModuleCard
                title={x.name}
                description={x.submodule}
                src={buildSrc(assetMap[x.name])}
              />
            </Grid>
          ))}
        </Grid>
        <FormControl component="fieldset" className={classes.container}>
          <FormLabel component="label" className={classes.label}>
            {t('Scheduled Assessments')}
          </FormLabel>
          <RadioGroup
            aria-label={t('Scheduled Assessments')}
            name="scheduleAssessments"
            value={state.scheduleType}
            onChange={onSelectionChange}
          >
            {assessmentsOptions.map(
              ({ label, value }, key): ReactElement => (
                <FormControlLabel
                  key={`language-option-${key}`}
                  value={value}
                  control={<Radio />}
                  label={label}
                />
              )
            )}
          </RadioGroup>
          <Box marginLeft="47px">
            {state.scheduleType === ScheduledActivityScheduleType.CALENDAR &&
              futureDates?.map((x: any) => (
                <Box display="flex">
                  <Box flex={1} display="flex" flexDirection="column">
                    {getModuleFromIds(x.moduleIds, x.date)?.map(m => (
                      <Box display="flex" flex={1} alignItems="center">
                        <Typography variant="body1" style={{ marginRight: 5 }}>
                          {format(new Date(x.date), DATE_FORMAT)} -
                        </Typography>
                        <Box
                          display="flex"
                          flex={1}
                          alignItems="center"
                          justifyContent="space-between"
                          height="30px"
                        >
                          <Typography variant="body1" style={{ marginRight: 5 }}>
                            {m.submodule}
                          </Typography>
                          {x.isFuture && (
                            <Fab
                              variant="outlined"
                              color="secondary"
                              size="small"
                              className={classes.datesContainerRemove}
                              startIcon={<CloseIcon fontSize="small" color="error" />}
                              onClick={() => deleteFutureModule(m)}
                            >
                              <span />
                            </Fab>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
          </Box>
        </FormControl>

        {state.scheduleType === ScheduledActivityScheduleType.CALENDAR && (
          <Box display="flex">
            <div className={classes.datesContainer}>
              {state.scheduledDates.map((x: any, idx: number) => (
                <Box
                  key={`${idx}-date-picker-custom-date`}
                  display="flex"
                  justifyContent="space-between"
                >
                  <Box display="flex" flexDirection="column">
                    <Typography>Date</Typography>
                    <DatePicker
                      disablePast
                      defaultValue={null}
                      style={{ width: 226, marginTop: 0, paddingRight: 5 }}
                      id={`date-picker-assessment-inline-${idx}`}
                      KeyboardButtonProps={{
                        'aria-label': t('change date assessments')
                      }}
                      inputProps={{
                        'aria-label': t('change date assessments')
                      }}
                      onChange={(newDate: Date | null): void => onDateChange(newDate, idx)}
                      value={x?.date ?? x}
                    />
                  </Box>

                  <Box display="flex">
                    <FormControl>
                      <Typography>{t('Modules')}</Typography>
                      <Select
                        defaultValue={t('Select a Module')}
                        variant="outlined"
                        style={{ width: 226 }}
                        SelectDisplayProps={{ style: { padding: 0, paddingLeft: 7 } }}
                        onChange={handleModuleChange(idx, state.scheduledDates[idx])}
                        renderValue={() => buildMultiSelected(idx, mState)}
                      >
                        <Typography
                          style={{
                            fontWeight: 400,
                            fontSize: 18,
                            color: '#696969',
                            paddingLeft: '16px'
                          }}
                        >
                          {t('Module')}
                        </Typography>
                        {modules?.map((x, midx) => (
                          <MenuItem value={x.id} key={`${x.name}_${midx}`}>
                            <Checkbox checked={mState[idx]?.[x.id]?.checked} />
                            <ListItemText primary={x.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  {state.scheduledDates.length > 1 && (
                    <Fab
                      variant="outlined"
                      color="secondary"
                      size="small"
                      className={classes.minusIcon}
                      startIcon={<RemoveIcon />}
                      onClick={handleDelete(idx)}
                    >
                      <span />
                    </Fab>
                  )}
                </Box>
              ))}
              <Box display="flex" justifyContent="center">
                {state.scheduledDates.length < 5 && (
                  <Typography component="p">
                    <Button
                      variant="text"
                      size="small"
                      color="primary"
                      disableElevation
                      onClick={() => {
                        onAddDate();
                        addAssessmentModule(mState, current.dropdownRowIdx + 1);
                      }}
                      startIcon={<AddCircleIcon />}
                    >
                      {t('Add Another Assessment')}
                    </Button>
                  </Typography>
                )}
              </Box>
            </div>
          </Box>
        )}
      </Fragment>
    </SettingsCard>
  );
};

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      padding: theme.spacing(2),
      width: 350
    },
    label: {
      color: theme.palette.common.black,
      fontWeight: theme.typography.fontWeightBold,
      marginBottom: theme.spacing(1),
      marginLeft: theme.spacing(-1.3),
      '&.Mui-focused': {
        color: `${theme.palette.common.black} !important`
      }
    },
    labelNoMargin: {
      marginLeft: 0
    },
    datesContainerRemove: {
      // marginLeft: theme.spacing(2),
      padding: theme.typography.pxToRem(14),
      height: theme.typography.pxToRem(24),
      width: theme.typography.pxToRem(24),
      fontSize: theme.typography.pxToRem(16),
      lineHeight: theme.typography.pxToRem(21),
      minWidth: 0
    },
    minusIcon: {
      alignSelf: 'flex-end',
      marginBottom: 10,
      marginLeft: theme.spacing(2),
      padding: `${theme.spacing(0.5)}px ${theme.spacing(2)}px`,
      height: theme.typography.pxToRem(33),
      fontSize: theme.typography.pxToRem(16),
      lineHeight: theme.typography.pxToRem(21),
      width: theme.typography.pxToRem(33),
      minWidth: theme.typography.pxToRem(33)
    },
    datesContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'column'
    },
    modal: {
      display: 'flex',
      padding: theme.spacing(1),
      alignItems: 'center',
      justifyContent: 'center'
    }
  };
});

export default AssessmentAccelerator;
